import { HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, Signal, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { AssetStateMessage } from '@r1-compass-library/models';
import { Play, PlayConfig } from '@r1-compass-library/models/play/play.model';
import { PlaySearch } from '@r1-compass-library/models/search/play-search.model';
import { PlayService } from '@r1-compass-library/services/play.service';
import { filterNil } from 'ngxtension/filter-nil';
import { exhaustMap, Observable, Subject, switchMap, map, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { DateTime, Duration } from 'luxon';
import { toSignal } from '@angular/core/rxjs-interop';
import { AssetStore } from '@r1-compass-library/store/asset.store';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';

export interface PlayState {
    play: Play | undefined;
    error: string | undefined;
    message: AssetStateMessage | undefined;
}

export const initialState: PlayState = {
    play: undefined,
    error: undefined,
    message: undefined,
};

@Injectable({
    providedIn: 'root',
})
export class PlayStore extends AssetStore<Play, PlayState> {
    readonly #playService = inject(PlayService);
    protected readonly route = inject(ActivatedRoute);

    play$ = this.select(state => state.play);
    play = this.selectSignal(state => state.play);
    message$ = this.select(state => state.message);
    playReset$ = new Subject<void>();

    assetTypeName = 'Play';

    shownComponent: Signal<'Builder' | 'Importer' | undefined> = toSignal(
        this.play$.pipe(
            filterNil(),
            map(play => {
                return play.type;
            })
        ),
        { initialValue: undefined }
    );

    timeUntilCanRunAgain = computed<number>(() => {
        if (this.play()?.lastSuccessfulRun) {
            //the IDE will complain about this, but lastSuccessfulRun is actually coming as a string instead of the Date it should be
            const lastSuccessfulRun = this.play()!.lastSuccessfulRun! as Date | string;
            let actualDate: Date;
            if (typeof lastSuccessfulRun === 'string') {
                actualDate = new Date(lastSuccessfulRun);
            } else if (lastSuccessfulRun instanceof Date) {
                actualDate = lastSuccessfulRun;
            } else {
                throw new Error('LastSuccessfulRun is not string or Date!');
            }
            const lastSuccessfulRunDateTime: DateTime = DateTime.fromJSDate(actualDate, { zone: 'utc' });
            const lastSuccessfulRunPlusTimeWindow = lastSuccessfulRunDateTime.plus({ hours: 2 });
            const now = DateTime.utc();
            const duration: Duration = lastSuccessfulRunPlusTimeWindow.diff(now);
            return duration.as('minute');
        }
        return 0;
    });

    constructor() {
        super(initialState);
    }

    reset = this.updater(state => ({
        ...initialState,
    }));

    setPlay = this.updater((state, play: Play) => ({
        ...state,
        play: play,
        isLoading: false,
    }));

    setError = this.updater((state, errorMessage: string) => ({
        ...state,
        isLoading: false,
        error: errorMessage,
    }));

    setMessage = this.updater((state, message: AssetStateMessage) => ({
        ...state,
        isLoading: false,
        message: message,
    }));

    resetMessage = this.updater(state => ({
        ...state,
        isLoading: false,
        message: undefined,
    }));

    getPlay = this.effect((id$: Observable<{ id?: number; iamDivisionId?: number }>) => {
        return id$.pipe(
            exhaustMap(id => {
                const userDivisionCodes: string[] = this.usersAvailableDivisions().map(
                    availableDivision => availableDivision.divisionCode
                );
                let search: PlaySearch;
                if (id.id) {
                    search = { id: id.id, divisions: userDivisionCodes };
                } else if (id.iamDivisionId) {
                    search = { iamDivisionId: id.iamDivisionId, divisions: userDivisionCodes };
                } else {
                    throw new Error('missing required id or iamDivisionId'); //TODO: update with error handler to break out
                }
                return this.#playService.search(search).pipe(
                    tapResponse({
                        next: plays => this.handleSearchResponse(plays, this.setPlay, this.setMessage),
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    savePlay = this.effect((play$: Observable<Partial<Play>>) => {
        return play$.pipe(
            filterNil(),
            exhaustMap(play => {
                const serviceCall: Observable<Play> = play.id
                    ? this.#playService.update(play.id, play)
                    : this.#playService.create(play);
                return serviceCall.pipe(
                    tapResponse({
                        next: ply => this.setPlay(ply),
                        error: (err: HttpErrorResponse) => console.error(err),
                        complete: () => this.playReset$.next(),
                    })
                );
            })
        );
    });

    savePlayData = rxMutation({
        operation: (playData: { id: number; config: Partial<PlayConfig> }) => {
            return this.#playService.updateData(playData.id, playData.config);
        },
        operator: exhaustOp,
        onSuccess: play => this.setPlay(play),
        onError: err => console.error(err),
    });

    savePlayAndData = rxMutation({
        operation: (saveData: { play: Partial<Play>; config: Partial<PlayConfig> }) => {
            const playCall = saveData.play.id
                ? this.handleUpdatePlayAndData(saveData.play.id, saveData.play, saveData.config)
                : this.handleCreatePlayAndData(saveData.play, saveData.config);
            return playCall;
        },
        operator: exhaustOp,
        onSuccess: (updatedPlayWithData: Play) => this.setPlay(updatedPlayWithData),
        onError: err => console.error(err),
    });

    private handleCreatePlayAndData(play: Partial<Play>, config: Partial<PlayConfig>) {
        play.config = config;
        return this.#playService.create(play);
    }

    private handleUpdatePlayAndData(playId: number, playToSave: Partial<Play>, config: Partial<PlayConfig>) {
        const oldPlay = this.play();
        const needsExpiryUpdate = !!oldPlay && oldPlay.enabled && !playToSave.enabled;
        //we update one at a time, starting with data, in order to prevent a race condition in the database persistence layer due to how
        //the merging of our data works currently
        return this.#playService.update(playId, playToSave).pipe(
            switchMap(() => this.#playService.updateData(playId, config)),
            concatMap(result => {
                if (needsExpiryUpdate) {
                    return this.#playService.updateExpiryData(playId).pipe(map(() => result));
                }
                return of(result);
            })
        );
    }
}
